/* global gdAlertify */
import React, { useState } from "react";
import axiosInstance from "../config/axiosConfig";
import GdTable from "./components/GdTable";
import { formatMoney } from "../utils/utils";

const monthNames = ["", "Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

const formatearPeriodoCorto = (periodoStr) => {
    if (!periodoStr) return "";
    const [year, month] = periodoStr.split("-");
    const nombreMes = monthNames[parseInt(month, 10)] || month;
    return `${nombreMes} ${year}`;
};

const renderMonto = (monto) => {
    if (!monto) return <span className="text-muted">$0</span>;
    return <span className="text-danger fw-bold">{`$${formatMoney(monto)}`}</span>;
};

const CobranzaClientes = () => {
    const [fecha, setFecha] = useState("");
    const [resultados, setResultados] = useState([]);
    const [periodosDeuda, setPeriodosDeuda] = useState([]);
    const [loading, setLoading] = useState(false);

    const buscarClientes = async () => {
        if (!fecha) return;
        gdAlertify.showLoader();
        setLoading(true);
        try {
            const params = { fecha };
            const response = await axiosInstance.get("/acuerdo-mutuo/cobranza/clientes", { params });
            setResultados(response.data.data || []);
            setPeriodosDeuda(response.data.periodos_deuda || []);
        } catch (error) {
            console.error("Error al buscar clientes a facturar:", error);
            gdAlertify.notiError("Error al cargar el listado de clientes a facturar");
        } finally {
            gdAlertify.hideLoader();
            setLoading(false);
        }
    };

    const hayCargosManuales = resultados.some(r => Number(r.deuda_cargos_manuales) > 0);

    // El nombre de cada columna es el mismo campo real de la fila (row[name]) — no solo algo
    // calculado dentro de render — porque GdTable ordena y filtra leyendo row[col.name]
    // directamente, sin pasar por el render. El backend ya entrega "deuda_<periodo>" como
    // un número plano en cada fila (no anidado) por esta misma razón.
    const columnasDeuda = periodosDeuda.map(periodo => ({
        title: `Deuda ${formatearPeriodoCorto(periodo)}`,
        name: `deuda_${periodo}`,
        type: "number",
        render: row => renderMonto(Number(row[`deuda_${periodo}`]) || 0)
    }));

    const columns = [
        { title: "RUT Paciente", name: "rut" },
        { title: "Paciente", name: "nombre_paciente" },
        {
            title: "Estado Paciente",
            name: "estado_paciente",
            render: row => Number(row.estado_paciente) === 1
                ? <span className="badge bg-success">Activo</span>
                : <span className="badge bg-danger">Retirado</span>
        },
        { title: "Email Notificaciones", name: "correos_notificacion" },
        { title: "Tutor Salud", name: "tutor_salud_nombre" },
        { title: "Email Tutor Salud", name: "tutor_salud_correo" },
        { title: "Tutor Pago", name: "tutor_pago_nombre" },
        { title: "Email Tutor Pago", name: "tutor_pago_correo" },
        { title: "Teléfono Tutor Pago", name: "tutor_pago_celular" },
        { title: "RUT Fact.", name: "fac_rut" },
        { title: "Razón Social Fact.", name: "fac_razon_social" },
        { title: "Giro Fact.", name: "fac_giro" },
        { title: "Email Fact.", name: "fac_email" },
        { title: "Dirección Fact.", name: "fac_direccion" },
        { title: "Comuna Fact.", name: "fac_comuna" },
        {
            title: "Importe a Facturar Este Mes",
            name: "importe_mes_actual",
            type: "number",
            render: row => <span className="fw-bold fs-6">{`$${formatMoney(row.importe_mes_actual)}`}</span>
        },
        ...columnasDeuda,
        ...(hayCargosManuales ? [{
            title: "Cargos Manuales",
            name: "deuda_cargos_manuales",
            type: "number",
            render: row => renderMonto(Number(row.deuda_cargos_manuales) || 0)
        }] : [])
    ];

    return (
        <div className="container-fluid mt-4">
            <div className="card">
                <div className="card-body">
                    <h3 className="mb-4">Clientes a Facturar / Deudores</h3>

                    <div className="row g-3 align-items-end">
                        <div className="col-md-6">
                            <label className="form-label">Mes</label>
                            <input
                                type="month"
                                className="form-control"
                                value={fecha}
                                onChange={e => setFecha(e.target.value)}
                            />
                        </div>
                        <div className="col-md-2">
                            <button
                                className="btn btn-primary w-100"
                                onClick={buscarClientes}
                                disabled={!fecha || loading}
                            >
                                {loading ? "Buscando..." : "Buscar"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card mt-3">
                <div className="card-body">
                    <GdTable
                        columns={columns}
                        data={resultados}
                        stickyHeader={true}
                        height={600}
                        headerHeight={50}
                        showCsvExport={true}
                        loading={loading}
                        exportFilename={`clientes-a-facturar-${fecha || "mes"}.xlsx`}
                    />
                </div>
            </div>
        </div>
    );
};

export default CobranzaClientes;
